import { useBetraegeSichtbar, verdeckt } from "../../../app/betraege";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { api } from "../../../data";
import { Dialog, PillView, useAction } from "../../../components/ui";
import { EmptyState } from "../../../components/States";
import { date, money as moneyFmt, today as heuteIn } from "../../../lib/format";
import { betragAlsText, parseCents } from "./formular";
import { BetragErkannt, betragUnlesbar } from "../../../components/Erkannt";
import {
  DEAL_STATUS, DEAL_STATUS_ORDER, anforderungsPruefung, callName, empfaengerDerCreatorRechnung, hiddenStatuses, labelMap, monthText, statusDef,
  statusPill, vorschlaege, vorschlagsHinweis, artikelVorschlagCents, type MonatInfo, type Vorschlag,
} from "./model";
import {
  archiviere, fordereRechnungAn, legeKampagneAn, offenePosten, pruefeArchiv, sendeNachricht, speichereKampagne, verknuepfeVertrag, type ArchivPruefung,
} from "./daten";
import type { CampaignRow, ContractLite, DealStatus, KoopDetail, StatusLabel, Substep } from "./typen";
import { CONTRACT_PILL, zwischenstaendeFuer } from "./model";
import { currentLang, t, ta } from "../../../lib/sprache";
import "./kooperationen.css";
import { chatLink } from "../../kommunikation/nachrichten/model";

/** Bausteine der Kooperationsseiten: Ansichtswahl, Rechtehinweis und Dialoge mit echten Schreibwegen. */

export const rechteText = () => t("Diese Aktion ist für dein Konto nicht freigegeben. Wende dich an deinen Admin.", "This action is not permitted for your account. Contact your admin.");

/** Für useAction: Erfolg ausdrücklich melden (run liefert bei Fehlern undefined, void-Schreibwege sonst auch). */
export function geklappt<T>(p: Promise<T>): Promise<true> {
  return p.then(() => true as const);
}

export type Ansicht = "liste" | "board" | "marke" | "beitraege";

/** Leerer Zustand der Kooperationen: ohne Creator zuerst „Creator anlegen“ (Befund 26), sonst „Kooperation anlegen“. */
export function KoopLeer({ talents }: { talents: readonly { status: string }[] }) {
  const ohneCreator = !talents.some((t) => t.status !== "archived");
  // K-573: ohne Creator führt „Neue Kooperation“ noch nicht weiter; Coco erklärt im Rundgang diesen leeren Zustand
  return ohneCreator
    ? <div className="tk-coco-leer-huelle" data-coco-leer="kooperation-anlegen"><EmptyState title={t("Hier beginnt dein nächster Deal.", "Your next deal starts here.")} leseTitel={t("Noch keine Kooperationen.", "No deals yet.")} text={t("Lege zuerst einen Creator an. Danach ordnest du ihm Kooperationen mit Marken zu.", "First create a creator. Then you assign deals with brands to them.")}
        action={<Link className="btn primary" to="/agentur/creator">{t("Creator anlegen", "Create creator")}</Link>} /></div>
    : <EmptyState title={t("Hier beginnt dein nächster Deal.", "Your next deal starts here.")} leseTitel={t("Noch keine Kooperationen.", "No deals yet.")} text={t("Lege eine Kooperation an und ordne Creator und Marke zu.", "Create a deal and assign creator and brand.")}
        action={<Link className="btn primary" to="/agentur/kooperationen/neu">{ta("Kooperation anlegen", "Create deal", "Deal anlegen", "Create deal")}</Link>} />;
}

/** Liste · Board · Nach Marke (deals-board.html: aktuelle Ansicht als primärer Knopf mit aria-current). */
export function Ansichtswahl({ aktuell, children }: { aktuell: Ansicht; children?: ReactNode }) {
  const item = (key: Ansicht, to: string, label: string) => (
    <Link className={aktuell === key ? "btn primary" : "btn"} to={to} aria-current={aktuell === key ? "page" : undefined}>{label}</Link>
  );
  // Revision 5: die vier Ansichten als zusammenhängender Umschalter (wie „Monat | Liste“ im Original-Kalender)
  return (
    <div className="row tk-kopf-aktionen">
      <div className="tk-ansicht" role="group" aria-label={t("Ansicht", "View")}>
        {item("liste", "/agentur/kooperationen", t("Liste", "List"))}
        {item("board", "/agentur/kooperationen/board", "Board")}
        {item("marke", "/agentur/kooperationen/nach-marke", t("Nach Marke", "By brand"))}
        {item("beitraege", "/agentur/kooperationen/beitraege", t("Beiträge", "Posts"))}
      </div>
      {children}
    </div>
  );
}

export function Rechtehinweis({ id }: { id: string }) {
  return <p id={id} className="source-note">{rechteText()}</p>;
}

/** Monatsstände als Pillen mit Zeichen und Text (AD-043 Monatsbalken). */
export function MonatsPillen({ monate, labels, mitBetrag, currency }: { monate: readonly MonatInfo[]; labels?: ReadonlyMap<string, string>; mitBetrag?: boolean; currency?: string }) {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  if (!monate.length) return <span className="muted">{t("Keine Monate angelegt.", "No months created.")}</span>;
  return (
    <div className="row">
      {monate.map((m) => {
        const p = statusPill(m.status, labels);
        return (
          <span key={m.period.id} className={`pill ${p.tone}`} title={monthText(m.month)}>
            {p.symbol} {monthText(m.month)} · {p.label}
            {mitBetrag && m.period.amount_cents != null ? ` · ${money(m.period.amount_cents, currency)}` : ""}
          </span>
        );
      })}
    </div>
  );
}

/** Auswahl eines Arbeitsstands (deal_status), eigene Bezeichnungen und ausgeblendete Stände der Agentur berücksichtigt. */
export function StatusDialog({ open, onClose, titel, text, aktuell, labels, onSave, busy, zwischen }: {
  open: boolean;
  onClose: () => void;
  titel: string;
  text: string;
  aktuell: DealStatus;
  labels: readonly StatusLabel[];
  /** true bei Erfolg (siehe geklappt); z: gewählter Zwischenstand (nur mit „zwischen“), undefined = unverändert */
  onSave: (s: DealStatus, z?: { id: string | null; seit: string | null }) => Promise<true | undefined>;
  busy: boolean;
  /** K-526: eigene Zwischenstände der Agentur und der aktuelle der Kooperation (nur an der Kooperation, nicht am Monat) */
  zwischen?: { substeps: readonly Substep[]; aktuell: string | null; seit: string | null };
}) {
  const [wert, setWert] = useState<DealStatus>(aktuell);
  const [zs, setZs] = useState<string>(zwischen?.aktuell ?? "");
  const [seit, setSeit] = useState<string>(zwischen?.seit ?? "");
  useEffect(() => { setWert(aktuell); setZs(zwischen?.aktuell ?? ""); setSeit(zwischen?.seit ?? ""); }, [aktuell, open]);
  const zsOptionen = zwischen ? zwischenstaendeFuer(wert, zwischen.substeps) : [];
  // gewählter Zwischenstand passt nicht mehr zum gewählten Oberstand: leeren
  const zsGueltig = zsOptionen.some((o) => o.id === zs) ? zs : "";
  const zsGeaendert = !!zwischen && (zsGueltig !== (zwischen.aktuell ?? "") || (zsGueltig !== "" && seit !== (zwischen.seit ?? "")));
  const own = labelMap(labels);
  const hidden = hiddenStatuses(labels);
  const options = DEAL_STATUS_ORDER.filter((s) => !hidden.has(s) || s === aktuell);
  return (
    <Dialog open={open} onClose={onClose} title={titel}>
      <p>{text}</p>
      <label>
        Status
        <select value={wert} onChange={(e) => setWert(e.target.value as DealStatus)}>
          {options.map((s) => <option key={s} value={s}>{statusDef(s, own).label}</option>)}
        </select>
      </label>
      {DEAL_STATUS[wert]?.terminal === "completed" && <div className="notice">{t("Abgeschlossen ist ein Endstatus. Solange ein Monat offen ist, bleibt die Kooperation in der Liste „Aktiv“.", "Completed is a final status. As long as a month is open, the deal stays in the “Active” list.")}</div>}
      {zwischen && zsOptionen.length > 0 && (
        <div className="grid">
          <label>
            {t("Zwischenstand (optional)", "Intermediate step (optional)")}
            <select value={zsGueltig} onChange={(e) => { setZs(e.target.value); if (e.target.value && !seit) setSeit(heuteIn()); }}>
              <option value="">{t("Keiner", "None")}</option>
              {zsOptionen.map((o) => <option key={o.id} value={o.id}>{(currentLang() === "en" && o.label_en?.trim()) || o.label_de}</option>)}
            </select>
          </label>
          {zsGueltig && <label>{t("Seit", "Since")}<input type="date" value={seit} max={heuteIn()} onChange={(e) => setSeit(e.target.value)} /></label>}
        </div>
      )}
      {zwischen && zsOptionen.length === 0 && zwischen.substeps.length === 0 && (
        <p className="muted">{t("Eigene Zwischenstände (z. B. „Ware beim Creator“) legst du unter Einstellungen · Vorlagen · Zwischenstände an.", "You can add your own intermediate steps (e.g. “Product with creator”) under Settings · Templates · Intermediate steps.")}</p>
      )}
      {wert === "invoicing" && <div className="notice">{t("Die Rechnung beim Creator forderst du je Monat über „Rechnung anfordern“ an. Erst dann bekommt der Creator eine Aufgabe und eine E-Mail.", "You request the invoice from the creator per month via “Request invoice”. Only then does the creator get a task and an email.")}</div>}
      <div className="row">
        <button className="primary" disabled={busy || (wert === aktuell && !zsGeaendert)} onClick={() => void onSave(wert, zsGeaendert ? { id: zsGueltig || null, seit: zsGueltig ? seit || null : null } : undefined).then((ok) => ok && onClose())}>{t("Status speichern", "Save status")}</button>
        <button onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
}

/** Nachricht an den Creator mit Verweis auf die Kooperation (chat_send). */
export function NachrichtDialog({ open, onClose, talentId, dealId, name }: { open: boolean; onClose: () => void; talentId: string; dealId: string; name: string }) {
  const [text, setText] = useState("");
  const { busy, error, run } = useAction();
  return (
    <Dialog open={open} onClose={onClose} title={t(`Nachricht an ${name}`, `Message to ${name}`)}>
      <p>{t("Die Nachricht erscheint im Chat mit ", "The message appears in the chat with ")}{name} {t("und verweist auf diese Kooperation.", "and refers to this deal.")}</p>
      <label>
        {t("Nachricht", "Message")}<textarea value={text} maxLength={4000} onChange={(e) => setText(e.target.value)} placeholder={t("Nachricht schreiben …", "Write a message …")} />
      </label>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button className="primary" disabled={busy !== null || !text.trim()}
          onClick={() => void run("senden", () => geklappt(sendeNachricht(api, talentId, text, dealId)), t("Nachricht gesendet.", "Message sent.")).then((r) => { if (r) { setText(""); onClose(); } })}>
          {t("Nachricht senden", "Send message")}</button>
        <Link className="btn" to={chatLink(talentId, dealId ? { kind: "deal", id: dealId } : null)}>{t("Zum Chat", "To the chat")}</Link>
        <button onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
}

/** Bestätigen einer Aktion mit Folgen (Archivieren, Speichern mit Rückfragen). */
export function BestaetigenDialog({ open, onClose, titel, children, bestaetigen, onConfirm, busy }: {
  open: boolean;
  onClose: () => void;
  titel: string;
  children: ReactNode;
  bestaetigen: string;
  onConfirm: () => void;
  busy?: boolean;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={titel}>
      {children}
      <div className="row">
        <button className="primary" disabled={busy} onClick={onConfirm}>{bestaetigen}</button>
        <button onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
}

/**
 * Zusatz zu einem wählbaren Vertrag: wird er Hauptvertrag oder zusätzlich verknüpft, und braucht es Recht und Grund
 * (VERTRAEGE-01)? Ein schon beendeter Vertrag (Ende ohne Verlängerung vor heute) deckt nur Aufträge bis zu seinem Ende
 * (VERTRAEGE-R2-05; die Datenbank weist spätere Kooperationen ab).
 */
export function vertragWahlText(c: Pick<ContractLite, "deal_id" | "status"> & Partial<Pick<ContractLite, "ends_on" | "auto_renew">>, today?: string): string {
  const ended = !!today && !!c.ends_on && !c.auto_renew && c.ends_on < today;
  return [c.deal_id ? t("gehört schon zu einer anderen Kooperation und gilt hier zusätzlich", "already belongs to another deal and applies here in addition") : t("wird Hauptvertrag dieser Kooperation", "becomes the main contract of this deal"),
    c.status === "signed" ? t("unterschrieben: mit Grund, steht in der Beweiskette", "signed: with reason, recorded in the audit trail") : null,
    ended ? t(`beendet am ${date(c.ends_on)}: deckt nur Aufträge bis dahin`, `ended on ${date(c.ends_on)}: only covers work up to then`) : null].filter(Boolean).join(" · ");
}

/**
 * Verträge mit der Kooperation verknüpfen und lösen (AD-062, Befund 14): auch versendete und unterschriebene, ein Vertrag
 * auch für mehrere Kooperationen. Der Vertragstext bleibt unverändert; ab dem Versand steht jede Zuordnung in der
 * Beweiskette. Unterschriebene Verträge ordnen nur Admins und Mitglieder mit Unterschriftsrecht um, mit Grund (0078).
 */
export function VertragDialog({ open, onClose, detail, canSign }: { open: boolean; onClose: () => void; detail: KoopDetail; canSign: boolean }) {
  const { busy, error, run } = useAction();
  const list = detail.freeContracts.filter((c) => c.status !== "void");
  const heute = heuteIn();
  const [wahl, setWahl] = useState("");
  const [loesen, setLoesen] = useState<string | null>(null);
  const [grund, setGrund] = useState("");
  useEffect(() => setWahl(list[0]?.id ?? ""), [open, list[0]?.id]);
  useEffect(() => setGrund(""), [wahl, loesen]);
  const verknuepft = detail.contracts.filter((c) => c.status !== "void");
  const gewaehlt = list.find((c) => c.id === wahl) ?? null;
  const grundNoetig = (c: ContractLite | null | undefined) => c?.status === "signed";
  const gesperrt = (c: ContractLite | null | undefined) => grundNoetig(c) && !canSign;
  const grundFeld = (
    <label>{t("Grund (steht in der Beweiskette)", "Reason (recorded in the audit trail)")}<textarea value={grund} onChange={(e) => setGrund(e.target.value)} minLength={5} /></label>
  );
  const rechteHinweis = <small>{t("Unterschriebene Verträge ordnen Admin und Mitglieder mit dem Recht „Verträge unterschreiben“ um.", "Signed contracts can be reassigned by admins and members with the “Sign contracts” permission.")}</small>;
  return (
    <Dialog open={open} onClose={onClose} title={t("Verträge der Kooperation", "Contracts of the deal")}>
      {verknuepft.length > 0 && (
        <div className="stack">
          <strong>{t("Verknüpft", "Linked")}</strong>
          {verknuepft.map((c) => (
            <div key={c.id} className="stack">
              <div className="task">
                <div>
                  <strong>{c.title}</strong>
                  <p>{c.link === "link" ? t("Gilt zusätzlich für diese Kooperation (Hauptvertrag einer anderen)", "Applies to this deal in addition (main contract of another one)") : t("Hauptvertrag dieser Kooperation", "Main contract of this deal")}</p>
                </div>
                <div className="row">
                  <PillView pill={CONTRACT_PILL[c.status]} />
                  {loesen === c.id ? (
                    <button className="danger" disabled={busy !== null || gesperrt(c) || (grundNoetig(c) && grund.trim().length < 5)}
                      onClick={() => void run("loesen", () => geklappt(verknuepfeVertrag(api, c.id, detail.deal.id, false, grund)), t("Verknüpfung gelöst.", "Link removed.")).then((r) => r && setLoesen(null))}>
                      {t("Wirklich lösen", "Really remove")}</button>
                  ) : <button onClick={() => setLoesen(c.id)}>{t("Lösen", "Remove")}</button>}
                </div>
              </div>
              {loesen === c.id && grundNoetig(c) && (gesperrt(c) ? rechteHinweis : grundFeld)}
            </div>
          ))}
        </div>
      )}
      <strong>{t("Weiteren Vertrag verknüpfen", "Link another contract")}</strong>
      <p>{t("Verträge von ", "Contracts of ")}{detail.talent ? callName(detail.talent) : t("diesem Creator", "this creator")} {t("und Verträge der Marke ohne Creator. Auch versendete und unterschriebene Verträge lassen sich zuordnen; der Vertragstext bleibt dabei unverändert.", "and the brand's contracts without a creator. Sent and signed contracts can also be assigned; the contract text stays unchanged.")}</p>
      {list.length === 0 && <p>{t("Es gibt keinen weiteren Vertrag. Lege einen Vertrag unter „Verträge“ an.", "There is no other contract. Create a contract under “Contracts”.")}</p>}
      {list.map((c: ContractLite) => (
        <label key={c.id} className="row tk-koop-wahl">
          <input type="radio" name="vertrag" value={c.id} checked={wahl === c.id} onChange={() => setWahl(c.id)} />
          <span className="stack"><span>{c.title}</span><span className="row"><PillView pill={CONTRACT_PILL[c.status]} /><small>{vertragWahlText(c, heute)}</small></span></span>
        </label>
      ))}
      {!loesen && grundNoetig(gewaehlt) && (gesperrt(gewaehlt) ? rechteHinweis : grundFeld)}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button className="primary" disabled={!wahl || busy !== null || gesperrt(gewaehlt) || (grundNoetig(gewaehlt) && grund.trim().length < 5)}
          onClick={() => void run("verknuepfen", () => geklappt(verknuepfeVertrag(api, wahl, detail.deal.id, true, grund)), t("Vertrag verknüpft.", "Contract linked.")).then((r) => r && onClose())}>
          {t("Vertrag verknüpfen", "Link contract")}</button>
        <Link className="btn" to="/agentur/vertraege">{t("Zu den Verträgen", "To the contracts")}</Link>
        <button onClick={onClose}>{t("Schließen", "Close")}</button>
      </div>
    </Dialog>
  );
}

const INVOICE_STATUS_TEXT: Record<string, string> = {
  get draft() { return t("Entwurf", "Draft"); }, get submitted() { return t("in Prüfung", "in review"); }, get revision_requested() { return t("Korrektur nötig", "Correction needed"); }, get approved() { return t("freigegeben", "approved"); }, get sent() { return t("offen", "open"); },
};

/**
 * Archivieren mit Prüfliste (Befund 21): offene Monate, Rechnungen, Anforderungen und Aufgaben je Kooperation. Mit offenen
 * Posten nur nach ausdrücklicher Bestätigung; sie bleiben in den Fristen und im Kalender sichtbar. Wiederherstellen geht immer.
 */
export function ArchivDialog({ open, onClose, deals, archiv, onDone }: {
  open: boolean;
  onClose: () => void;
  deals: readonly { id: string; label: string }[];
  archiv: boolean;
  onDone?: () => void;
}) {
  const { busy, error, run } = useAction();
  const [pruefung, setPruefung] = useState<ArchivPruefung[] | null>(null);
  const [bestaetigt, setBestaetigt] = useState(false);
  const ids = deals.map((d) => d.id).join(",");
  useEffect(() => {
    setBestaetigt(false);
    setPruefung(null);
    if (!open || !archiv) return;
    let aktiv = true;
    void pruefeArchiv(api, deals.map((d) => d.id)).then((r) => aktiv && setPruefung(r)).catch(() => aktiv && setPruefung([]));
    return () => {
      aktiv = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, archiv, ids]);
  const offen = (pruefung ?? []).filter((p) => offenePosten(p) > 0);
  const label = (id: string) => deals.find((d) => d.id === id)?.label ?? t("Kooperation", "Deal");
  const n = deals.length;
  return (
    <Dialog open={open} onClose={onClose} title={archiv ? (n === 1 ? t("Kooperation archivieren?", "Archive deal?") : t(`${n} Kooperationen archivieren?`, `Archive ${n} deals?`)) : (n === 1 ? t("Kooperation wiederherstellen?", "Restore deal?") : t(`${n} Kooperationen wiederherstellen?`, `Restore ${n} deals?`))}>
      {!archiv && <p>{n === 1 ? t("Die Kooperation erscheint", "The deal appears") : t("Die Kooperationen erscheinen", "The deals appear")} {ta("wieder in den Listen und, falls freigegeben, im Creator-Portal.", "again in the lists and, if released, in the creator portal.", "wieder in den Listen.", "again in the lists.")}</p>}
      {archiv && (
        <>
          <p>{t("Archiviert wird nicht gelöscht. Der Creator sieht archivierte Kooperationen nicht mehr; Rechnungen, Verträge und Monate bleiben erhalten.", "Archived is not deleted. The creator no longer sees archived deals; invoices, contracts and months are kept.")}</p>
          {pruefung === null && <p className="muted" role="status">{t("Offene Posten werden geprüft …", "Checking open items …")}</p>}
          {pruefung !== null && offen.length === 0 && <p><span className="pill success"><span className="pill-zeichen">✓</span> {t("Nichts mehr offen", "Nothing open anymore")}</span></p>}
          {offen.map((p) => (
            <div key={p.deal_id} className="notice stack">
              <strong>{label(p.deal_id)}</strong>
              <ul>
                {p.open_months > 0 && <li>{p.open_months === 1 ? t("1 Monat", "1 month") : t(`${p.open_months} Monate`, `${p.open_months} months`)} {t("noch nicht abgerechnet: ", "not yet invoiced: ")}{p.details.months.map((m) => monthText(m)).join(", ")}</li>}
                {p.open_invoices > 0 && <li>{p.open_invoices === 1 ? t("1 offene Rechnung", "1 open invoice") : t(`${p.open_invoices} offene Rechnungen`, `${p.open_invoices} open invoices`)}: {p.details.invoices.map((i) => `${i.number ?? t("ohne Nummer", "without number")} (${INVOICE_STATUS_TEXT[i.status] ?? i.status})`).join(", ")}</li>}
                {p.open_requests > 0 && <li>{t("Rechnung beim Creator angefordert, noch nicht eingereicht: ", "Invoice requested from the creator, not yet submitted: ")}{p.details.requests.map((m) => monthText(m)).join(", ")}{t(". Die Anforderung wird beim Archivieren erledigt.", ". The request is closed when archiving.")}</li>}
                {p.open_tasks > 0 && <li>{p.open_tasks === 1 ? t("1 offene Aufgabe", "1 open task") : t(`${p.open_tasks} offene Aufgaben`, `${p.open_tasks} open tasks`)} {t("in der Glocke", "in the bell")}</li>}
              </ul>
            </div>
          ))}
          {offen.length > 0 && (
            <label className="row tk-koop-wahl">
              <input type="checkbox" checked={bestaetigt} onChange={(e) => setBestaetigt(e.target.checked)} />
              <span>{t("Ich habe die offenen Posten geprüft. Rechnungen und Fristen bleiben unter „Archiv“ und im Kalender sichtbar.", "I have checked the open items. Invoices and deadlines remain visible under “Archive” and in the calendar.")}</span>
            </label>
          )}
        </>
      )}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button className="primary" disabled={busy !== null || (archiv && (pruefung === null || (offen.length > 0 && !bestaetigt)))}
          onClick={() => void run("archiv", () => geklappt(archiviere(api, deals.map((d) => d.id), archiv, bestaetigt)), archiv ? t("Archiviert.", "Archived.") : t("Wiederhergestellt.", "Restored."))
            .then((r) => {
              if (r) {
                onDone?.();
                onClose();
              }
            })}>
          {archiv ? (offen.length ? t("Trotzdem archivieren", "Archive anyway") : t("Archivieren", "Archive")) : t("Wiederherstellen", "Restore")}
        </button>
        <button onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
}

/** Kampagne anlegen oder ändern (Befund 22): Titel, gemeinsames Briefing, Budget, Deadline, Stand. */
export function KampagnenDialog({ open, onClose, kampagne, neu, onSaved }: {
  open: boolean;
  onClose: () => void;
  kampagne: CampaignRow | null;
  /** Für eine neue Kampagne: Agentur, Marke und Währung */
  neu?: { orgId: string; brandId: string; brandName: string; currency: string };
  onSaved?: (c: CampaignRow) => void;
}) {
  const { busy, error, run } = useAction();
  const ausKampagne = () => ({
    title: kampagne?.title ?? "", briefing: kampagne?.briefing ?? "", budget: betragAlsText(kampagne?.budget_cents),
    deadline: kampagne?.deadline ?? "", status: kampagne?.status ?? ("active" as CampaignRow["status"]),
  });
  const [f, setF] = useState(ausKampagne);
  const [fehler, setFehler] = useState<string | null>(null);
  // Nur beim Öffnen oder Wechsel der Kampagne neu befüllen, nicht bei jedem Nachladen der Daten
  useEffect(() => {
    setFehler(null);
    setF(ausKampagne());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kampagne?.id]);
  const speichern = () => {
    const budget = f.budget.trim() ? parseCents(f.budget) : null;
    if (!f.title.trim()) return setFehler(t("Bitte einen Titel eingeben.", "Please enter a title."));
    if (budget != null && (Number.isNaN(budget) || budget < 0)) return setFehler(t("Das Budget ist kein gültiger Betrag.", "The budget is not a valid amount."));
    setFehler(null);
    const d = { title: f.title.trim(), briefing: f.briefing.trim() || null, budget_cents: budget, deadline: f.deadline || null, status: f.status };
    void run("kampagne", () => (kampagne ? speichereKampagne(api, kampagne.id, d) : legeKampagneAn(api, neu!.orgId, neu!.brandId, neu!.currency, d)),
      kampagne ? t("Kampagne gespeichert.", "Campaign saved.") : t("Kampagne angelegt.", "Campaign created.")).then((c) => {
      if (c) {
        onSaved?.(c);
        onClose();
      }
    });
  };
  return (
    <Dialog open={open} onClose={onClose} title={kampagne ? t(`Kampagne „${kampagne.title}“`, `Campaign “${kampagne.title}”`) : t(`Neue Kampagne${neu ? ` · ${neu.brandName}` : ""}`, `New campaign${neu ? ` · ${neu.brandName}` : ""}`)}>
      <p className="source-note">{t("Eine Kampagne fasst mehrere Kooperationen derselben Marke zusammen: gemeinsames Briefing, Budget und Deadline, eine Sammelrechnung an die Marke.", "A campaign combines several deals of the same brand: shared briefing, budget and deadline, one collective invoice to the brand.")}</p>
      <label>{t("Titel", "Title")}<input value={f.title} maxLength={120} onChange={(e) => setF((x) => ({ ...x, title: e.target.value }))} placeholder={t("z. B. Launch Frühjahr", "e.g. spring launch")} /></label>
      <label>Briefing<textarea value={f.briefing} maxLength={8000} onChange={(e) => setF((x) => ({ ...x, briefing: e.target.value }))} placeholder={t("Ziel, Kernbotschaft, Pflichtangaben, Hashtags …", "Goal, key message, mandatory details, hashtags …")} /></label>
      <div className="grid">
        <label>{t("Budget netto in ", "Budget net in ")}{kampagne?.currency ?? neu?.currency ?? "EUR"}<input inputMode="decimal" value={f.budget} aria-invalid={betragUnlesbar(f.budget) || undefined} onChange={(e) => setF((x) => ({ ...x, budget: e.target.value }))} /><BetragErkannt text={f.budget} currency={kampagne?.currency ?? neu?.currency ?? "EUR"} /></label>
        <label>Deadline<input type="date" value={f.deadline} onChange={(e) => setF((x) => ({ ...x, deadline: e.target.value }))} /></label>
        {kampagne && (
          <label>
            {t("Stand", "Status")}<select value={f.status} onChange={(e) => setF((x) => ({ ...x, status: e.target.value as CampaignRow["status"] }))}>
              <option value="active">{t("Läuft", "Running")}</option>
              <option value="done">{t("Abgeschlossen", "Completed")}</option>
              <option value="cancelled">{t("Abgesagt", "Declined")}</option>
            </select>
          </label>
        )}
      </div>
      {(fehler || error) && <p className="tk-error" role="alert">{fehler ?? error}</p>}
      <div className="row">
        <button className="primary" disabled={busy !== null} onClick={speichern}>{kampagne ? t("Kampagne speichern", "Save campaign") : t("Kampagne anlegen", "Create campaign")}</button>
        <button onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
}

/**
 * Rechnung beim Creator anfordern (AD-046 … AD-048): Empfänger, Prüfung der Pflichtangaben (blockiert nie, KA-18),
 * Briefing und vorgeschlagene Positionen für genau einen Monat.
 */
export function AnforderungDialog({ open, onClose, detail, monat, ratePercent, orgName, stand, today, nachkaufSeit }: {
  open: boolean;
  onClose: () => void;
  detail: KoopDetail;
  monat: MonatInfo;
  ratePercent: number | null;
  orgName: string;
  /** Leistungsstand des Monats und heute: Anforderung vor erbrachter Leistung ist „wichtig“ (Befund 13) */
  stand?: string;
  today?: string;
  /** Nachberechnung: nur Positionen nach der Rechnung des Creators vorschlagen (0060) */
  nachkaufSeit?: string | null;
}) {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const { busy, error, run } = useAction();
  const empfaenger = useMemo(() => empfaengerDerCreatorRechnung(detail.deal, detail.brand, detail.settings, orgName, detail.brands), [detail, orgName]);
  const pruefung = useMemo(() => anforderungsPruefung({
    deal: detail.deal, period: monat.period, talent: detail.talent, empfaenger, settings: detail.settings, iban: detail.iban, hasPortalAccount: detail.hasPortalAccount,
    stand, today, beitraege: detail.beitraege,
  }), [detail, monat, empfaenger, stand, today]);
  // Startwerte nur beim Öffnen (der Dialog wird je Öffnen neu eingehängt); ein Neuladen im Hintergrund setzt nichts zurück
  const [note, setNote] = useState(() => monat.period.request_note ?? (nachkaufSeit ? t("Nachberechnung: bitte eine eigene Rechnung nur über die neuen Positionen stellen.", "Supplementary invoice: please issue a separate invoice covering only the new items.") : ""));
  const [items, setItems] = useState<(Vorschlag & { an: boolean })[]>(() =>
    vorschlaege(detail.deal, monat.period, detail.periods, detail.items, ratePercent, { nurNach: nachkaufSeit ?? null }).map((v) => ({ ...v, an: true })));
  const [artikel, setArtikel] = useState("");
  const [eigen, setEigen] = useState({ text: "", betrag: "" });
  const again = !!monat.period.invoice_requested_at;
  const eigenCents = parseCents(eigen.betrag);
  // Gegenprüfung GB-4: ein unlesbarer Betrag sperrt „Hinzufügen“ und steht als Meldung unter dem Feld (statt still nichts zu tun)
  const eigenUnlesbar = betragUnlesbar(eigen.betrag);
  const addEigen = () => {
    if (!eigen.text.trim() || eigenUnlesbar || (eigenCents != null && (Number.isNaN(eigenCents) || eigenCents < 0))) return;
    setItems((l) => [...l, { description: eigen.text.trim(), quantity: 1, amount_cents: eigenCents, commission_free: false, an: true }]);
    setEigen({ text: "", betrag: "" });
  };
  const currency = detail.deal.currency;
  const addArticle = () => {
    const a = detail.articles.find((x) => x.id === artikel);
    if (!a) return;
    setItems((l) => [...l, { description: a.description?.trim() ? `${a.name} · ${a.description}` : a.name, quantity: 1, amount_cents: artikelVorschlagCents(detail.deal, a.default_price_cents, a.commissionable, ratePercent), commission_free: !a.commissionable, an: true }]);
    setArtikel("");
  };
  const gruppe = (titel: string, punkte: typeof pruefung.pflicht, tone: "error" | "warning" | "info") => punkte.length > 0 && (
    <div className="stack">
      <strong>{titel}</strong>
      {punkte.map((p) => (
        <div key={p.code} className="task">
          <div>
            <span className={`pill ${tone}`}><span className="pill-zeichen">{tone === "error" ? "!" : tone === "warning" ? "◷" : "◇"}</span> {p.text}</span>
            {p.wo && <p>{t("Nachtragen unter ", "Add under ")}<Link to={p.wo.to}>{p.wo.label}</Link></p>}
          </div>
        </div>
      ))}
    </div>
  );
  const offen = pruefung.pflicht.length + pruefung.wichtig.length;
  return (
    <Dialog open={open} onClose={onClose} title={t(`Rechnung anfordern · ${monthText(monat.month)}`, `Request invoice · ${monthText(monat.month)}`)} wide>
      <div className="notice">
        <strong>{t("Rechnung geht an:", "Invoice goes to:")}</strong> {empfaenger.name}
        {empfaenger.zeilen.length > 0 && <><br />{empfaenger.zeilen.join(", ")}</>}
        {empfaenger.hinweis && <><br />{empfaenger.hinweis}</>}
      </div>
      {again && <p>{t("Für ", "For ")}{monthText(monat.month)} {t("wurde am ", "was withdrawn on ")}{date(monat.period.invoice_requested_at, detail.settings?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone)} {t("schon angefordert. Eine erneute Anforderung erreicht den Creator noch einmal.", "already requested. Requesting again reaches the creator once more.")}</p>}
      {gruppe(t("Pflichtangaben", "Mandatory details"), pruefung.pflicht, "error")}
      {gruppe(t("Wichtig", "Important"), pruefung.wichtig, "warning")}
      {gruppe(t("Hinweise", "Notices"), pruefung.hinweise, "info")}
      {pruefung.ibanUnbekannt && <p className="source-note">{t("Die IBAN des Creators ist nur mit dem Recht „Auszahlung“ sichtbar und wurde nicht geprüft.", "The creator's IBAN is only visible with the “Payout” permission and has not been checked.")}</p>}
      {offen === 0 && pruefung.hinweise.length === 0 && <p><span className="pill success"><span className="pill-zeichen">✓</span> {t("Alle Angaben vorhanden", "All details available")}</span></p>}
      <label>
        {t("Hinweis an ", "Note to ")}{detail.talent ? callName(detail.talent) : t("den Creator", "the creator")} (optional)
        <textarea value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} placeholder={t("z. B. Reisekosten bitte als eigene Position mit Beleg.", "e.g. please list travel costs as a separate item with receipt.")} />
      </label>
      <div className="stack">
        <strong>{t("Vorgeschlagene Positionen", "Suggested items")}</strong>
        <p className="source-note">{vorschlagsHinweis(detail.deal)}</p>
        {items.map((v, i) => (
          <label key={i} className="row">
            <input type="checkbox" checked={v.an} onChange={(e) => setItems((l) => l.map((x, j) => (j === i ? { ...x, an: e.target.checked } : x)))} />
            {v.description}
            {v.amount_cents != null ? ` · ${money(v.amount_cents, currency)}` : ""}
            {v.commission_free ? t(" · provisionsfrei", " · commission-free") : ""}
          </label>
        ))}
        {items.length === 0 && <p className="muted">{t("Keine Positionen vorgeschlagen.", "No items suggested.")}</p>}
        {detail.articles.length > 0 && (
          <div className="row">
            <label>
              {t("Aus Artikelliste", "From item list")}<select value={artikel} onChange={(e) => setArtikel(e.target.value)}>
                <option value="">{t("Artikel wählen …", "Choose item …")}</option>
                {detail.articles.map((a) => <option key={a.id} value={a.id}>{a.name}{a.default_price_cents != null ? ` · ${money(a.default_price_cents, a.currency ?? currency)}` : ""}</option>)}
              </select>
            </label>
            <button type="button" disabled={!artikel} onClick={addArticle}>{t("Hinzufügen", "Add")}</button>
          </div>
        )}
        <div className="row">
          <label>{t("Eigene Position", "Own item")}<input type="text" value={eigen.text} onChange={(e) => setEigen((x) => ({ ...x, text: e.target.value }))} placeholder={t("Beschreibung", "Description")} /></label>
          <label>{t("Betrag netto (optional)", "Amount net (optional)")}<input inputMode="decimal" value={eigen.betrag} aria-invalid={eigenUnlesbar || undefined} onChange={(e) => setEigen((x) => ({ ...x, betrag: e.target.value }))} /><BetragErkannt text={eigen.betrag} currency={currency} /></label>
          <button type="button" disabled={!eigen.text.trim() || eigenUnlesbar} onClick={addEigen}>{t("Hinzufügen", "Add")}</button>
        </div>
      </div>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button className="primary" disabled={busy !== null}
          onClick={() => void run("anfordern", () => fordereRechnungAn(api, monat.period.id, note, items.filter((x) => x.an).map(({ an: _an, ...v }) => v)),
            `${again ? t("Rechnung erneut angefordert.", "Invoice requested again.") : t("Rechnung angefordert.", "Invoice requested.")} ${detail.talent?.email
              ? t("Der Creator sieht sie in seiner Glocke und bekommt eine Mail.", "The creator sees it in their bell and gets an email.")
              : t("Beim Creator ist keine E-Mail-Adresse hinterlegt: Er sieht die Anforderung nur im Portal, falls er dort ein Konto hat. Bitte sag ihm sonst selbst Bescheid.", "No email address is on file for the creator: they only see the request in the portal, if they have an account there. Otherwise please let them know yourself.")}`).then((r) => r && onClose())}>
          {offen ? t(`Trotz ${offen === 1 ? t("1 offenem Punkt", "1 open point") : t(`${offen} offenen Punkten`, `${offen} open points`)} anfordern`, `Request despite ${offen === 1 ? t("1 offenem Punkt", "1 open point") : t(`${offen} offenen Punkten`, `${offen} open points`)}`) : again ? t("Erneut anfordern", "Request again") : t("Rechnung anfordern", "Request invoice")}
        </button>
        <button onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
}
